import { subHours } from "date-fns"
import {
  BaseUserType,
  ExpressionResultToken,
  IntegrationStatus,
  ListUserSurveySummariesForSponsorQuery,
  ReportingParticipantStatus,
  RespondentType,
  SponsorParticipantEventStatus,
  SurveySignatureStatus,
  UserSurveyDataAvailability,
  UserSurveySummaryStatus,
} from "src/gql/graphql"
import { uuid } from "@myvp/shared/src/functions/uuid"
import { sleep } from "@myvp/shared/src/functions/sleep"
import * as participantsService from "src/pages/study/participants/participants.service"
import { mockTimeConstants } from "@myvp/shared/src/constants/mock-time.constants"

const mockParticipantEventsAndGroups: Record<
  string,
  Awaited<ReturnType<typeof participantsService.getParticipantEventsAndGroups>>
> = {
  default: {
    participantNumber: "103-003",
    participantStatus: ReportingParticipantStatus.Active,
    participantGroups: [
      {
        groupCategory: "Age",
        selectedGroup: "> 18",
      },
      {
        groupCategory: "Sex",
        selectedGroup: "female",
      },
      {
        groupCategory: "Cohort",
        selectedGroup: "1",
      },
      {
        groupCategory: "Cohort",
        selectedGroup: "2",
      },
      {
        groupCategory: "Cohort",
        selectedGroup: "3",
      },
      {
        groupCategory: "Cohort",
        selectedGroup: "4",
      },
    ],
    participantEvents: [
      {
        participantEventId: "ecoaActivated",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "eCOA Activated",
        eventDate: "2023-11-05T10:00Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "screeningVisit",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Screening Visit",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.System,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "baseline",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Baseline",
        eventDate: "2023-11-08T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "treatmentDay1",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Treatment Day 1",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "treatmentDay2",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Treatment Day 2",
        eventDate: null,
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: false,
        didNotOccur: true,
      },
      {
        participantEventId: "treatmentDay3",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Treatment Day 3",
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "criteriaCheckWithLogs",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Criteria Check With Logs",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: true,
        didNotOccur: false,
      },
      {
        participantEventId: "criteriaCheckWithLogsAndOutboundApiCall",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Criteria Check With Logs And Outbound API Call",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: true,
        didNotOccur: false,
      },
      {
        participantEventId: "criteriaCheckWithRestrictedLogsAndAccess",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Criteria Check With Restricted Logs and Access",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: true,
        didNotOccur: false,
      },
      {
        participantEventId: "criteriaCheckWithRestrictedLogsNoAccess",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Criteria Check With Restricted Logs Without Access",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: true,
        didNotOccur: false,
      },
      {
        participantEventId: "criteriaCheckNotAssociatedWithRule",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Criteria Check Not Connected To Rule",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: true,
        didNotOccur: false,
      },
      {
        participantEventId: "deactivatedCriteriaCheckWithLogs",
        participantEventStatus: SponsorParticipantEventStatus.Inactive,
        eventName: "Deactivated Criteria Check With Logs",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: true,
        didNotOccur: false,
      },
      {
        participantEventId: "deactivatedCriteriaCheckWithoutLogs",
        participantEventStatus: SponsorParticipantEventStatus.Inactive,
        eventName: "Deactivated Criteria Check Without Logs",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "criteriaCheckWithoutLogs",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Criteria Check Without Logs",
        eventDate: "2023-11-05T16:08Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        siteTimeZone: "America/Chicago",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "studyWithdrawn",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Study Withdrawn",
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "studyComplete",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Study Complete",
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
    ],
  },
  "uuid-inactive": {
    participantNumber: "103-004",
    participantStatus: ReportingParticipantStatus.Inactive,
    participantGroups: [],
    participantEvents: [
      {
        participantEventId: "ecoaActivated",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "eCOA Activated",
        eventDate: "2023-11-05T10:00Z",
        populatedBy: BaseUserType.User,
        expectedDateTime: "2023-11-05T10:00Z",
        windowDaysAfter: 2,
        windowDaysBefore: 2,
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "studyWithdrawn",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Study Withdrawn",
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
      {
        participantEventId: "studyComplete",
        participantEventStatus: SponsorParticipantEventStatus.Active,
        eventName: "Study Complete",
        hasCriteriaCheckEventLogs: false,
        didNotOccur: false,
      },
    ],
  },
}

const mockCriteriaCheckEventLogs: Record<
  string,
  Awaited<
    ReturnType<typeof participantsService.listCriteriaCheckEventLogsForSponsor>
  >
> = {
  default: {
    isRestrictingData: false,
    isAssociatedWithRule: true,
    criteriaCheckEventLogs: [
      {
        preconditionsSuccess: true,
        evaluatedAt: subHours(new Date(), 5).toISOString(),
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "Subject has a baseline weekly average NPRS score of ≥4 points",
            expressionResultToken: ExpressionResultToken.No,
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "Number of completed NPRS surveys (out of seven): ",
            expressionResultValue: "7",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "The average score of compliant NPRS surveys was: ",
            expressionResultValue: "4.21",
          },
        ],
      },
      {
        preconditionsSuccess: false,
        evaluatedAt: subHours(new Date(), 8).toISOString(),
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "Subject has a baseline weekly average NPRS score of ≥4 points",
            expressionResultToken: ExpressionResultToken.Yes,
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "Number of completed NPRS surveys (out of seven): ",
            expressionResultValue: "5",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "The average score of compliant NPRS surveys was: ",
            expressionResultValue: "6.23",
          },
        ],
      },
    ],
    canAccessRestrictedData: false,
    hasRestrictedLogs: false,
  },
  criteriaCheckWithLogs: {
    isRestrictingData: false,
    isAssociatedWithRule: true,
    criteriaCheckEventLogs: [
      {
        preconditionsSuccess: true,
        evaluatedAt: subHours(new Date(), 5).toISOString(),
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultSection",
            displayLabel: "Section 1",
          },
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "Subject has a baseline weekly average NPRS score of ≥4 points",
            expressionResultToken: ExpressionResultToken.No,
          },
          {
            __typename: "CriteriaCheckResultSection",
            displayLabel: "Section 2",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "Number of completed NPRS surveys (out of seven): ",
            expressionResultValue: "7",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "The average score of compliant NPRS surveys was: ",
            expressionResultValue: "4.21",
          },
        ],
      },
      {
        preconditionsSuccess: false,
        evaluatedAt: subHours(new Date(), 8).toISOString(),
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "Subject has a baseline weekly average NPRS score of ≥4 points",
            expressionResultToken: ExpressionResultToken.Yes,
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "Number of completed NPRS surveys (out of seven): ",
            expressionResultValue: "5",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "The average score of compliant NPRS surveys was: ",
            expressionResultValue: "6.23",
          },
        ],
      },
    ],
    canAccessRestrictedData: false,
    hasRestrictedLogs: false,
  },
  criteriaCheckWithLogsAndOutboundApiCall: {
    isRestrictingData: false,
    isAssociatedWithRule: true,
    criteriaCheckEventLogs: [
      {
        preconditionsSuccess: true,
        evaluatedAt: subHours(new Date(), 5).toISOString(),
        integrationStatus: IntegrationStatus.Success,
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultSection",
            displayLabel: "Section 1",
          },
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "Subject has a baseline weekly average NPRS score of ≥4 points",
            expressionResultToken: ExpressionResultToken.No,
          },
          {
            __typename: "CriteriaCheckResultSection",
            displayLabel: "Section 2",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "Number of completed NPRS surveys (out of seven): ",
            expressionResultValue: "7",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "The average score of compliant NPRS surveys was: ",
            expressionResultValue: "4.21",
          },
        ],
      },
      {
        preconditionsSuccess: false,
        evaluatedAt: subHours(new Date(), 8).toISOString(),
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "Subject has a baseline weekly average NPRS score of ≥4 points",
            expressionResultToken: ExpressionResultToken.Yes,
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "Number of completed NPRS surveys (out of seven): ",
            expressionResultValue: "5",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "The average score of compliant NPRS surveys was: ",
            expressionResultValue: "6.23",
          },
        ],
      },
    ],
    canAccessRestrictedData: false,
    hasRestrictedLogs: false,
  },
  criteriaCheckWithRestrictedLogsAndAccess: {
    isRestrictingData: false,
    isAssociatedWithRule: true,
    criteriaCheckEventLogs: [
      {
        preconditionsSuccess: true,
        evaluatedAt: subHours(new Date(), 5).toISOString(),
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "Subject has a baseline weekly average NPRS score of ≥4 points",
            expressionResultToken: ExpressionResultToken.No,
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "Number of completed NPRS surveys (out of seven): ",
            expressionResultValue: "7",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "The average score of compliant NPRS surveys was: ",
            expressionResultValue: "4.21",
          },
        ],
      },
    ],
    canAccessRestrictedData: true,
    hasRestrictedLogs: true,
  },
  criteriaCheckWithRestrictedLogsNoAccess: {
    isRestrictingData: false,
    isAssociatedWithRule: true,
    criteriaCheckEventLogs: [
      {
        preconditionsSuccess: true,
        evaluatedAt: subHours(new Date(), 5).toISOString(),
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "This log is not restricted so the user can still see it",
            expressionResultToken: ExpressionResultToken.Yes,
          },
        ],
      },
    ],
    canAccessRestrictedData: false,
    hasRestrictedLogs: true,
  },
  criteriaCheckNotAssociatedWithRule: {
    isRestrictingData: false,
    isAssociatedWithRule: false,
    criteriaCheckEventLogs: [
      {
        preconditionsSuccess: true,
        evaluatedAt: subHours(new Date(), 5).toISOString(),
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "Subject has a baseline weekly average NPRS score of ≥4 points",
            expressionResultToken: ExpressionResultToken.No,
          },
        ],
      },
    ],
    canAccessRestrictedData: false,
    hasRestrictedLogs: false,
  },
  deactivatedCriteriaCheckWithLogs: {
    isRestrictingData: false,
    isAssociatedWithRule: true,
    criteriaCheckEventLogs: [
      {
        preconditionsSuccess: true,
        evaluatedAt: subHours(new Date(), 5).toISOString(),
        criteriaCheckResults: [
          {
            __typename: "CriteriaCheckResultToken",
            displayLabel:
              "Subject has a baseline weekly average NPRS score of ≥4 points",
            expressionResultToken: ExpressionResultToken.No,
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "Number of completed NPRS surveys (out of seven): ",
            expressionResultValue: "7",
          },
          {
            __typename: "CriteriaCheckResultValue",
            displayLabel: "The average score of compliant NPRS surveys was: ",
            expressionResultValue: "4.21",
          },
        ],
      },
    ],
    canAccessRestrictedData: false,
    hasRestrictedLogs: false,
  },
}

export const getParticipantEventsAndGroups: typeof participantsService.getParticipantEventsAndGroups =
  async (args) => {
    return (
      mockParticipantEventsAndGroups[args.studyParticipantId] ??
      mockParticipantEventsAndGroups.default
    )
  }

export const listCriteriaCheckEventLogsForSponsor: typeof participantsService.listCriteriaCheckEventLogsForSponsor =
  async (args) => {
    return (
      mockCriteriaCheckEventLogs[args.criteriaCheckEventId] ??
      mockCriteriaCheckEventLogs.default
    )
  }

export const listUserSurveySummariesForSponsor: typeof participantsService.listUserSurveySummariesForSponsor =
  async (args) => {
    const userSurveySummaryStatuses = Array.from(
      Object.values(UserSurveySummaryStatus)
    )
    const respondentTypes = Array.from(Object.values(RespondentType))
    const surveyDataAvailabilities = Array.from(
      Object.values(UserSurveyDataAvailability)
    )
    const signatureStatuses = Array.from(Object.values(SurveySignatureStatus))
    const surveyCount = args.studyParticipantId === "uuid-inactive" ? 0 : 2500
    const userSurveySummaries = Array.from(new Array(surveyCount)).map(
      (_, index) => {
        return {
          userSurveyId: uuid(),
          surveyLabel: `survey-${index + 1}`,
          surveyStatus:
            userSurveySummaryStatuses[index % userSurveySummaryStatuses.length],
          event: {
            eventLabel: "eCOA Activated",
          },
          respondent: (() => {
            const respondentType =
              respondentTypes[index % respondentTypes.length]
            switch (respondentType) {
              case RespondentType.Participant:
                return { __typename: "ParticipantRespondent" as const }
              case RespondentType.Caregiver:
                return {
                  __typename: "CaregiverRespondent" as const,
                  relationshipToParticipant: "Father",
                }
              case RespondentType.SiteUser:
                return { __typename: "SiteUserRespondent" as const }
            }
          })(),
          actionDateTime: new Date().toISOString(),
          actionTimezone: "America/New_York",
          isRestricted: index % 10 === 0,
          dataAvailability:
            surveyDataAvailabilities[index % surveyDataAvailabilities.length],
          dataChangeRequestId:
            index % 5 === 1 ? "data-change-request-id" : null,
          signatureStatus: signatureStatuses[index % signatureStatuses.length],
          partLabel: (() => {
            if (index % 2 === 0) {
              return `part-${index + 1}`
            }
            return undefined
          })(),
          parentUserSurveyId: "parent-user-survey-id",
        }
      }
    ) satisfies ListUserSurveySummariesForSponsorQuery["listUserSurveySummariesForSponsor"]["userSurveySummaries"]

    await sleep(mockTimeConstants.smallWaitTime)

    return {
      participantNumber: "1",
      pageOutputParams: {
        totalElements: userSurveySummaries.length,
      },
      userSurveySummaries: userSurveySummaries.slice(
        args.paginationInput.offset,
        args.paginationInput.offset + args.paginationInput.pageSize
      ),
    }
  }

export const getSurveyFilterOptionsForSponsor: typeof participantsService.getSurveyFilterOptionsForSponsor =
  async () => {
    return {
      surveyFilterOptions: [
        {
          surveyLabel:
            "Very Long Survey Name Extra Suffix Text That Keeps Going",
          surveyIds: ["test-1"],
        },
        {
          surveyLabel: "Another Very Long Label With Even More Text Here",
          surveyIds: ["test-2"],
        },
        {
          surveyLabel: "Survey Label",
          surveyIds: ["surveyid1"],
        },
        {
          surveyLabel: "Survey Label Multi Ids",
          surveyIds: ["surveyid2", "surveyid3", "surveyid4"],
        },
        {
          surveyLabel: "Survey Label 5",
          surveyIds: ["surveyid5"],
        },
        {
          surveyLabel: "Survey Label 6",
          surveyIds: ["surveyid6"],
        },
        {
          surveyLabel: "Survey Label 7",
          surveyIds: ["surveyid7"],
        },
        {
          surveyLabel: "Survey Label 8",
          surveyIds: ["surveyid8"],
        },
        {
          surveyLabel: "Survey Label 9",
          surveyIds: ["surveyid9"],
        },
        {
          surveyLabel: "Survey Label 10",
          surveyIds: ["surveyid10"],
        },
      ],
      eventFilterOptions: [
        {
          eventLabel: "eCOA Activated",
          unscheduledEventInstance: 1,
          participantEvents: [
            {
              eventLabel: "Study Withdrawn",
              studyEventGroupId: "study-withdrawn",
              unscheduledEventInstance: 1,
              collectionIds: ["ecoa-collection"],
            },
            {
              eventLabel: "Study Complete",
              studyEventGroupId: "study-complete",
              unscheduledEventInstance: 1,
              collectionIds: ["ecoa-collection"],
            },
          ],
        },
      ],
      statusFilterOptions: [
        UserSurveySummaryStatus.Available,
        UserSurveySummaryStatus.Completed,
        UserSurveySummaryStatus.Missed,
        UserSurveySummaryStatus.IntentionallyLeftBlank,
        UserSurveySummaryStatus.Transcribed,
        UserSurveySummaryStatus.Late,
      ],
      respondentFilterOptions: [
        RespondentType.Participant,
        RespondentType.Caregiver,
        RespondentType.SiteUser,
      ],
      signatureStatusFilterOptions: [
        SurveySignatureStatus.SignatureNeeded,
        SurveySignatureStatus.Signed,
        SurveySignatureStatus.NotApplicable,
      ],
    }
  }

export const participantsMock = {
  participantEventsAndGroups: mockParticipantEventsAndGroups,
  criteriaCheckEventLogs: mockCriteriaCheckEventLogs,
}
